import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFlag } from '@rsos/flags';
import './defs';
import { findCommittedActionCommand, matchActionCommands } from './matcher';
import { parseActionCommand } from './parser';
import { getAllActionCommands } from './registry';
import { isStateCatalogEndpointConfigured } from './stateLayerCatalog';
import type {
  ActionArgSuggestion,
  ActionCommandDef,
  ActionExecuteResult,
  ParsedActionCommand,
} from './types';
import useActionContext from './useActionContext';

/**
 * Shape returned by `useActionMode`. The host
 * (`ActionCommandExperience`) uses this to drive the action palette
 * UI. `executeArg` is exposed when the committed action defines
 * `execute`; absent otherwise (the host falls back to autofill in
 * that case).
 */
export interface ActionModeState {
  /** Always defined; reflects the current input verbatim. */
  parsed: ParsedActionCommand;
  /**
   * Action commands matching the typed prefix. Empty array means
   * "no action matches" — the palette renders an empty state pointing
   * back to the bare-`//` catalog.
   */
  suggestions: ActionCommandDef[];
  /**
   * The action command whose id/alias exactly equals the parsed
   * name. `undefined` while the user is still typing the name.
   */
  committedAction: ActionCommandDef | undefined;
  /**
   * True when the palette should swap from rendering the action
   * catalog to rendering live argument autocomplete rows. Driven by:
   *   1. A committed action.
   *   2. The user has typed past the action name (trailing space).
   */
  isArgMode: boolean;
  /**
   * Result of the committed action's `getArgSuggestions`. Computed
   * every render while in arg mode; empty in any other state.
   */
  argSuggestions: ActionArgSuggestion[];
  /**
   * Run the committed action's `execute` against a picked
   * suggestion. Returns `undefined` when:
   *   - no action is committed, OR
   *   - the committed action does not define `execute` (suggestion-
   *     only mode — host should autofill instead).
   * Throws never; failures are reported via the result's `ok: false`
   * branch so the host can keep the palette interactive.
   */
  executeArg: (
    suggestion: ActionArgSuggestion,
  ) => Promise<ActionExecuteResult> | undefined;
}

/**
 * Resolve the user's `//`-prefixed input into actionable state for
 * the host. Mirrors the shape of `useCommandMode` (the slash
 * equivalent) intentionally so palette code stays straightforward.
 */
export default function useActionMode(input: string): ActionModeState {
  const ctx = useActionContext();
  const parsed = useMemo(() => parseActionCommand(input), [input]);
  const [, setArgSuggestionsVersion] = useState(0);
  const isAddActionEnabled = useFlag('gis_action_mode');
  const isStateCatalogConfigured = isStateCatalogEndpointConfigured();
  const isAddActionAvailable = isAddActionEnabled && isStateCatalogConfigured;

  const allActions = useMemo(() => getAllActionCommands(), []);

  const suggestions = useMemo(
    () =>
      matchActionCommands({ parsed, defs: allActions }).filter(
        def => def.id !== 'add' || isAddActionAvailable,
      ),
    [parsed, allActions, isAddActionAvailable],
  );

  const committedAction = useMemo(() => {
    const committed = findCommittedActionCommand({ parsed, defs: allActions });
    if (committed?.id === 'add' && !isAddActionAvailable) return undefined;
    return committed;
  }, [parsed, allActions, isAddActionAvailable]);

  // Same `trailingSpace` gate as the slash system: hold off on arg-
  // suggestion mode until the user has visibly moved past the name,
  // otherwise the palette would briefly flicker into arg mode the
  // moment a name matched.
  const isArgMode = Boolean(committedAction && parsed.trailingSpace);

  useEffect(() => {
    if (!isArgMode || !committedAction?.subscribeArgSuggestionsUpdates) {
      return undefined;
    }

    return committedAction.subscribeArgSuggestionsUpdates(() => {
      setArgSuggestionsVersion(version => version + 1);
    });
  }, [isArgMode, committedAction]);

  let argSuggestions: ActionArgSuggestion[] = [];
  if (isArgMode && committedAction) {
    try {
      // `ctx.getState()` is read inside the def, so the rows refresh
      // whenever a keystroke re-runs this hook. External updates can
      // also trigger via `argSuggestionsVersion` when defs subscribe.
      argSuggestions = committedAction.getArgSuggestions(parsed, ctx);
    } catch {
      // A buggy provider shouldn't crash the search bar; treat it
      // the same as "no matches" and let the user keep typing.
      argSuggestions = [];
    }
  }

  const executeArg = useCallback(
    (
      suggestion: ActionArgSuggestion,
    ): Promise<ActionExecuteResult> | undefined => {
      if (!committedAction || !committedAction.execute) return undefined;
      if (committedAction.id === 'add' && !isAddActionAvailable) {
        return Promise.resolve({
          ok: false,
          message: 'Not available: //add',
        });
      }
      try {
        const result = committedAction.execute(suggestion, ctx);
        return Promise.resolve(result).catch(err => ({
          ok: false,
          message: err instanceof Error ? err.message : String(err),
        }));
      } catch (err) {
        return Promise.resolve({
          ok: false,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [committedAction, ctx, isAddActionAvailable],
  );

  return {
    parsed,
    suggestions,
    committedAction,
    isArgMode,
    argSuggestions,
    executeArg,
  };
}
